import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API, formatMoney, formatMoney2, formatNumber } from "../lib/api";
import { BannerFarmanaliza } from "../components/BannerFarmanaliza";
import { useSortable, SortHeader } from "../lib/sort";
import { ExportButtons } from "../lib/exports";
import DrillDown from "../components/DrillDown";
import { periodoVentas } from "../lib/periodo";
import { useRowHover } from "../components/RowHover";
import { RankingLaboratorios } from "../components/RankingLaboratorios";
import { Loader2, Package, AlertTriangle, Filter, RotateCcw, Download } from "lucide-react";

const VIEWS = {
  farmacia: {
    title: "Stock parado · Farmacia",
    subtitle: "Productos de clase D con CN igual o superior a 400000 (medicamento y especialidad).",
    segment: "farmacia", abcd: "D", caducar: false,
  },
  parafarmacia: {
    title: "Stock parado · Parafarmacia",
    subtitle: "Productos de clase D con CN por debajo de 400000 (parafarmacia y complementos).",
    segment: "parafarmacia", abcd: "D", caducar: false,
  },
  caducar: {
    title: "A punto de caducar",
    subtitle: "Productos con fecha de caducidad dentro del plazo elegido, ordenados por valor.",
    segment: "", abcd: "", caducar: true,
  },
};

const HORIZONTES = [30, 60, 90, 180, 365];

function today() {
  return new Date().toISOString().slice(0, 10);
}
function plusDays(n) {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}
const fmtDate = (v) => (v ? String(v).slice(0, 10).split("-").reverse().join("/") : "—");

export default function StockParado() {
  const { view = "farmacia" } = useParams();
  const cfg = VIEWS[view] || VIEWS.farmacia;

  const [studies, setStudies] = useState([]);
  const [studyId, setStudyId] = useState(null);
  const [study, setStudy] = useState(null);
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [familia, setFamilia] = useState("");
  const [famDrill, setFamDrill] = useState(null);
  const [laboratorio, setLaboratorio] = useState("");
  const [search, setSearch] = useState("");
  const [entradaFrom, setEntradaFrom] = useState("");
  const [entradaTo, setEntradaTo] = useState("");
  const [cadFrom, setCadFrom] = useState("");
  const [cadTo, setCadTo] = useState("");
  const [horizonte, setHorizonte] = useState(90);
  const [dReal, setDReal] = useState(true);
  const [searchInput, setSearchInput] = useState("");

  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/studies");
        setStudies(data);
        setStudyId((prev) => prev || data[0]?.id || null);
      } catch (e) {
        setErr("No se pudieron cargar los estudios");
      } finally { setLoading(false); }
    })();
  }, []);

  // Reset de filtros al cambiar de submenú
  useEffect(() => {
    setFamilia(""); setLaboratorio(""); setSearch(""); setSearchInput("");
    setEntradaFrom(""); setEntradaTo("");
    const hasta = new URLSearchParams(window.location.search).get("hasta");
    if (cfg.caducar && hasta) { setCadFrom(""); setCadTo(hasta); setHorizonte(null); }
    else if (cfg.caducar) { setCadFrom(today()); setCadTo(plusDays(90)); setHorizonte(90); }
    else { setCadFrom(""); setCadTo(""); }
  }, [view]); // eslint-disable-line

  useEffect(() => {
    if (!studyId) return;
    (async () => {
      const { data } = await api.get(`/studies/${studyId}`);
      setStudy(data);
    })();
  }, [studyId]);

  const params = useMemo(() => {
    const p = {};
    if (cfg.abcd) p.abcd = cfg.abcd;
    if (cfg.segment) p.segment = cfg.segment;
    if (familia) p.familia = familia;
    if (laboratorio) p.laboratorio = laboratorio;
    if (search) p.search = search;
    if (entradaFrom) p.entrada_from = entradaFrom;
    if (entradaTo) p.entrada_to = entradaTo;
    if (cadFrom) p.cad_from = cadFrom;
    if (cadTo) p.cad_to = cadTo;
    if (cfg.caducar) p.con_caducidad = true;
    p.con_stock = true;
    if (!cfg.caducar && dReal) p.d_real = true;
    return p;
  }, [cfg, familia, laboratorio, search, entradaFrom, entradaTo, cadFrom, cadTo, dReal]);

  useEffect(() => {
    if (!studyId) return;
    let ignore = false;
    (async () => {
      try {
        const [s, l] = await Promise.all([
          api.get(`/studies/${studyId}/stock-parado`, { params }),
          api.get(`/studies/${studyId}/products`, {
            params: { ...params, limit: 500, sort_by: cfg.caducar ? "caducidad" : "valor_pmc", sort_dir: cfg.caducar ? "asc" : "desc" },
          }),
        ]);
        if (ignore) return;
        setSummary(s.data); setItems(l.data.items); setTotal(l.data.total); setErr("");
      } catch (e) {
        if (!ignore) setErr("No se pudo cargar la selección");
      }
    })();
    return () => { ignore = true; };
  }, [studyId, JSON.stringify(params)]); // eslint-disable-line

  const sort = useSortable(items, null, "desc");
  const hov = useRowHover();
  const famSort = useSortable(summary?.by_familia || [], "valor_pmc", "desc");

  const exportUrl = () => {
    const qs = new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v)]));
    return `${API}/studies/${studyId}/export/selection?${qs.toString()}`;
  };

  const cols = [
    { header: "CN", key: "cn" }, { header: "Descripción", key: "descripcion" },
    { header: "Familia", key: "familia" }, { header: "Laboratorio", key: "laboratorio" },
    { header: "Stock", key: "stock", align: "right" },
    { header: "Valor PMC (€)", key: "valor_pmc", format: (v) => (v || 0).toFixed(2), align: "right" },
    { header: "Valor PVP (€)", key: "valor_pvp", format: (v) => (v || 0).toFixed(2), align: "right" },
    { header: "Última entrada", key: "u_entrada", format: fmtDate },
    { header: "Uds vendidas", key: "uds_vendidas", align: "right" },
    { header: "Última salida", key: "u_salida", format: fmtDate },
    { header: "Caducidad", key: "caducidad", format: fmtDate },
  ];

  if (loading) return <div className="p-8 flex gap-2 text-slate-500"><Loader2 className="animate-spin" size={18} /> Cargando…</div>;
  if (!studies.length) {
    return (
      <div className="p-8" data-testid="stock-parado-empty">
        <div className="card p-10 text-center text-slate-500">
          <Package size={32} className="mx-auto text-slate-300" />
          <p className="mt-3 font-semibold">Todavía no tienes ningún estudio de stock</p>
          <Link to="/upload" className="btn btn-primary mt-4">Subir estudio</Link>
        </div>
      </div>
    );
  }

  const t = summary?.totals || {};

  return (
    <div className="p-8 fade-in" data-testid="stock-parado-page">
      <BannerFarmanaliza compact image="/img/farmanaliza-inventario.jpg?v=2" kicker="Inventario" subtitle="Stock parado"
                         text="Detecta el stock que no rota, cuánto dinero inmoviliza y qué conviene mover o devolver." testid="banner-inventario" />
      <div className="mb-6">
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Stock parado</div>
        <h1 className="font-display text-4xl font-extrabold mt-1 tracking-tight">{cfg.title}</h1>
        <p className="text-slate-600 mt-1 text-sm">{cfg.subtitle}</p>
        {!cfg.caducar && (
          <div className="mt-4 inline-flex rounded-full border border-slate-200 bg-white p-1" data-testid="d-real-toggle">
            {[{ v: true, label: "D real", hint: "sin ventas y última entrada de hace más de un año" }, { v: false, label: "Todo el D", hint: "todo lo que no se ha vendido en el año" }].map((o) => (
              <button key={o.label} onClick={() => setDReal(o.v)} title={o.hint} data-testid={o.v ? "d-real-btn" : "d-todo-btn"}
                      className={"px-4 py-1.5 rounded-full text-xs font-bold transition-colors " + (dReal === o.v ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900")}>
                {o.label}
              </button>
            ))}
          </div>
        )}
        {!cfg.caducar && (
          <p className="text-xs text-slate-500 mt-2" data-testid="d-real-hint">
            {dReal ? "D real: sin ventas en el año y con la última entrada de hace más de un año (los productos sin fecha de entrada no cuentan)." : "Todo el D: todos los productos sin ventas en el año del estudio."}
          </p>
        )}
      </div>

      {err && <div className="card mb-6 border border-red-300 bg-red-50 px-5 py-3 text-sm text-red-700" data-testid="stock-parado-error">{err}</div>}

      <div className="card p-5 mb-6" data-testid="filters-panel">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-3">
          <Filter size={12} /> Filtros
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs font-semibold text-slate-600">Estudio</label>
            <select className="input w-full mt-1" value={studyId || ""} onChange={(e) => setStudyId(e.target.value)} data-testid="study-select">
              {studies.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Familia</label>
            <select className="input w-full mt-1" value={familia} onChange={(e) => setFamilia(e.target.value)} data-testid="familia-select">
              <option value="">Todas las familias</option>
              {(study?.families || []).map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Laboratorio</label>
            <select className="input w-full mt-1" value={laboratorio} onChange={(e) => setLaboratorio(e.target.value)} data-testid="laboratorio-select">
              <option value="">Todos los laboratorios</option>
              {(study?.labs || []).map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Buscar producto o CN</label>
            <input className="input w-full mt-1" value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
                   placeholder="Ej. ibuprofeno" data-testid="search-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Última entrada desde</label>
            <input type="date" className="input w-full mt-1" value={entradaFrom} onChange={(e) => setEntradaFrom(e.target.value)} data-testid="entrada-from" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Última entrada hasta</label>
            <input type="date" className="input w-full mt-1" value={entradaTo} onChange={(e) => setEntradaTo(e.target.value)} data-testid="entrada-to" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Caduca desde</label>
            <input type="date" className="input w-full mt-1" value={cadFrom} onChange={(e) => setCadFrom(e.target.value)} data-testid="cad-from" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Caduca hasta</label>
            <input type="date" className="input w-full mt-1" value={cadTo} onChange={(e) => setCadTo(e.target.value)} data-testid="cad-to" />
          </div>
        </div>
        <div className="flex items-center gap-2 mt-4 flex-wrap">
          {cfg.caducar && HORIZONTES.map((h) => (
            <button key={h} onClick={() => { setHorizonte(h); setCadFrom(today()); setCadTo(plusDays(h)); }}
                    className={"px-3 py-1.5 rounded-lg text-xs font-bold border transition " +
                      (horizonte === h ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-slate-400")}
                    data-testid={`horizonte-${h}`}>
              Próximos {h} días
            </button>
          ))}
          <button onClick={() => {
            setFamilia(""); setLaboratorio(""); setSearch(""); setSearchInput("");
            setEntradaFrom(""); setEntradaTo("");
            if (cfg.caducar) { setCadFrom(today()); setCadTo(plusDays(90)); setHorizonte(90); } else { setCadFrom(""); setCadTo(""); }
          }} className="btn btn-outline btn-sm" data-testid="reset-filters">
            <RotateCcw size={13} /> Limpiar filtros
          </button>
          <a href={exportUrl()} target="_blank" rel="noreferrer" className="btn btn-outline btn-sm" data-testid="export-selection">
            <Download size={13} /> Excel de toda la selección
          </a>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="card p-5" data-testid="kpi-productos">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Productos</div>
          <div className="font-display text-2xl font-extrabold mt-1">{formatNumber(t.productos)}</div>
        </div>
        <div className="card p-5" data-testid="kpi-unidades">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Unidades en stock</div>
          <div className="font-display text-2xl font-extrabold mt-1">{formatNumber(t.stock)}</div>
        </div>
        <div className="card p-5" data-testid="kpi-valor-pmc">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Dinero inmovilizado (PMC)</div>
          <div className="font-display text-2xl font-extrabold mt-1 text-amber-700">{formatMoney(t.valor_pmc)}</div>
        </div>
        <div className="card p-5" data-testid="kpi-valor-pvp">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Valor a PVP</div>
          <div className="font-display text-2xl font-extrabold mt-1">{formatMoney(t.valor_pvp)}</div>
        </div>
      </div>

      {summary?.by_familia?.length > 0 && (
        <div className="card p-6 mb-6" data-testid="by-familia-panel">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Desglose</div>
              <h3 className="font-display text-xl font-bold">Por familia</h3>
              <p className="text-xs text-slate-500 mt-1">Pulsa una familia para filtrar el listado.</p>
            </div>
            <ExportButtons filename="stock-parado-familias" title={cfg.title} sheetName="Familias"
                           columns={[{ header: "Familia", key: "label" }, { header: "Productos", key: "productos", align: "right" },
                                     { header: "Stock", key: "stock", align: "right" },
                                     { header: "Valor PMC (€)", key: "valor_pmc", format: (v) => (v || 0).toFixed(2), align: "right" },
                                     { header: "Valor PVP (€)", key: "valor_pvp", format: (v) => (v || 0).toFixed(2), align: "right" }]}
                           rows={famSort.sortedRows} testIdPrefix="export-familias" />
          </div>
          <div className="overflow-x-auto max-h-80">
            <table className="dense" data-testid="familias-table">
              <thead>
                <tr>
                  <th><SortHeader label="Familia" k="label" sort={famSort} defaultDir="asc" /></th>
                  <th className="num"><SortHeader label="Productos" k="productos" sort={famSort} num /></th>
                  <th className="num"><SortHeader label="Stock" k="stock" sort={famSort} num /></th>
                  <th className="num"><SortHeader label="Valor PMC" k="valor_pmc" sort={famSort} num /></th>
                  <th className="num"><SortHeader label="Valor PVP" k="valor_pvp" sort={famSort} num /></th>
                </tr>
              </thead>
              <tbody>
                {famSort.sortedRows.map((f) => (
                  <tr key={f.label} onClick={() => setFamDrill(f.label)}
                      className={"cursor-pointer " + (familia === f.label ? "bg-slate-100 font-semibold" : "")}
                      data-testid={`familia-row-${f.label}`}>
                    <td>{f.label}</td>
                    <td className="num">{formatNumber(f.productos)}</td>
                    <td className="num">{formatNumber(f.stock)}</td>
                    <td className="num text-amber-700 font-semibold">{formatMoney2(f.valor_pmc)}</td>
                    <td className="num">{formatMoney2(f.valor_pvp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card p-6" data-testid="products-panel">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Listado</div>
            <h3 className="font-display text-xl font-bold">
              {formatNumber(total)} productos{total > items.length ? ` · mostrando los ${items.length} de mayor valor` : ""}
            </h3>
          </div>
          <ExportButtons filename="stock-parado" title={cfg.title} sheetName="StockParado" orientation="landscape"
                         columns={cols} rows={sort.sortedRows} testIdPrefix="export-products" />
        </div>
        <div className="overflow-x-auto max-h-[36rem]">
          {hov.tip}
          <table className="dense" data-testid="products-table">
            <thead>
              <tr>
                <th><SortHeader label="CN" k="cn" sort={sort} defaultDir="asc" /></th>
                <th><SortHeader label="Descripción" k="descripcion" sort={sort} defaultDir="asc" /></th>
                <th><SortHeader label="Familia" k="familia" sort={sort} defaultDir="asc" /></th>
                <th><SortHeader label="Laboratorio" k="laboratorio" sort={sort} defaultDir="asc" /></th>
                <th className="num"><SortHeader label="Stock" k="stock" sort={sort} num /></th>
                <th className="num"><SortHeader label="Valor PMC" k="valor_pmc" sort={sort} num /></th>
                <th className="num"><SortHeader label="Valor PVP" k="valor_pvp" sort={sort} num /></th>
                <th className="num"><SortHeader label="Uds vend." k="uds_vendidas" sort={sort} num /></th>
                <th><SortHeader label="Últ. entrada" k="u_entrada" sort={sort} defaultDir="asc" /></th>
                <th><SortHeader label="Últ. salida" k="u_salida" sort={sort} defaultDir="asc" /></th>
                <th><SortHeader label="Caducidad" k="caducidad" sort={sort} defaultDir="asc" /></th>
              </tr>
            </thead>
            <tbody>
              {sort.sortedRows.map((p) => {
                const soon = p.caducidad && String(p.caducidad).slice(0, 10) <= plusDays(90);
                return (
                  <tr key={p.cn} data-testid={`product-row-${p.cn}`} className="cursor-help" {...hov.bind(p)}>
                    <td className="font-mono text-xs">{p.cn}</td>
                    <td className="text-xs">{p.descripcion}</td>
                    <td className="text-xs text-slate-500">{p.familia}</td>
                    <td className="text-xs text-slate-500">{p.laboratorio === "nan" ? "—" : p.laboratorio}</td>
                    <td className="num">{formatNumber(p.stock)}</td>
                    <td className="num font-semibold text-amber-700">{formatMoney2(p.valor_pmc)}</td>
                    <td className="num">{formatMoney2(p.valor_pvp)}</td>
                    <td className="num">{formatNumber(p.uds_vendidas || 0)}</td>
                    <td className="text-xs">{fmtDate(p.u_entrada)}</td>
                    <td className="text-xs text-slate-500">{fmtDate(p.u_salida)}</td>
                    <td className={"text-xs " + (soon ? "text-red-700 font-bold" : "")}>
                      {soon && <AlertTriangle size={11} className="inline mr-1" />}{fmtDate(p.caducidad)}
                    </td>
                  </tr>
                );
              })}
              {sort.sortedRows.length === 0 && (
                <tr><td colSpan={10} className="text-center text-slate-500 py-8" data-testid="no-products">
                  No hay productos con estos filtros
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {view === "parafarmacia" && (
        <RankingLaboratorios rows={summary?.by_laboratorio} title={cfg.title} selected={laboratorio}
                             onPick={(l) => { setLaboratorio(l); window.scrollTo({ top: 0, behavior: "smooth" }); }} />
      )}
      <DrillDown open={!!famDrill} onClose={() => setFamDrill(null)} studyId={studyId} periodo={periodoVentas(study)}
                 title={`${famDrill || ""} · ${cfg.title}`} subtitle="Productos de la familia con los filtros de esta página"
                 filters={{ ...Object.fromEntries(Object.entries(params).filter(([k]) => k !== "familia")), familia: famDrill }} />
    </div>
  );
}
